import React, { useState } from "react";
import { FiSave, FiTrash2, FiEdit2, FiPlay, FiPlusCircle, FiDownload } from "react-icons/fi";
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import PerformanceModeToggle from './PerformanceModeToggle';

// A single-letter mnemonic badge (P/B/S), matching the same badges used in
// the formatting toolbar under the menu - see LetterIcon in DocumentBody.js.
function LetterIcon({ children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        borderRadius: 4,
        backgroundColor: "rgba(0,0,0,0.06)",
        fontWeight: 700,
        fontSize: 11,
        lineHeight: 1,
      }}
    >
      {children}
    </span>
  );
}

export default function EditorMenu({
  onSave,
  onEdit,
  onPerform,
  onPractice,
  onFormat,
  onAddBits,
  onCoachReview,
  onExportDocx,
  saving,
  setConfirmDelete,
  mode,
  revisions = [],
  onSelectRevision,
  aiDisabled = false,
  rawView = false,
  onSetRawView,
  lineFocused = false,
  isHeaderActive = false,
  onApplyBold,
  onApplyBit,
  onApplyStage,
}) {
  const [anchorElFile, setAnchorElFile] = useState(null);
  const [anchorElAI, setAnchorElAI] = useState(null);
  const [anchorElRev, setAnchorElRev] = useState(null);
  const [anchorElEdit, setAnchorElEdit] = useState(null);
  const [anchorElViewMenu, setAnchorElViewMenu] = useState(null);

  const openFile = Boolean(anchorElFile);
  const openAI = Boolean(anchorElAI);
  const openRev = Boolean(anchorElRev);
  const openEdit = Boolean(anchorElEdit);
  const openViewMenu = Boolean(anchorElViewMenu);

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center">
        {/* File Menu */}
        <Button
          variant="text"
          onClick={e => setAnchorElFile(e.currentTarget)}
          sx={{ textTransform: 'none', fontWeight: 'bold', color: 'gray' }}
        >
          File
        </Button>
        <Menu
          anchorEl={anchorElFile}
          open={openFile}
          onClose={() => setAnchorElFile(null)}
        >
          <MenuItem onClick={() => { setAnchorElFile(null); onSave && onSave(); }} disabled={saving}>
            <ListItemIcon><FiSave /></ListItemIcon>
            <Typography>Save</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setAnchorElFile(null); onExportDocx && onExportDocx(); }}>
            <ListItemIcon><FiDownload /></ListItemIcon>
            <Typography>Export...</Typography>
          </MenuItem>
          <Divider />
          {revisions.length > 0 && (
            <MenuItem
              onClick={e => setAnchorElRev(e.currentTarget)}
            >
              <ListItemIcon><FiEdit2 /></ListItemIcon>
              <Typography>Revisions</Typography>
            </MenuItem>
          )}
          <Menu
            anchorEl={anchorElRev}
            open={openRev}
            onClose={() => setAnchorElRev(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {revisions.length === 0 && (
              <MenuItem disabled>
                <Typography color="textSecondary">No revisions</Typography>
              </MenuItem>
            )}
            {revisions.slice().reverse().map((revId, idx) => (
              <MenuItem
                key={revId}
                onClick={() => {
                  onSelectRevision(revId);
                  setAnchorElRev(null);
                  setAnchorElFile(null);
                }}
              >
                {`Rev #${revisions.length - idx}...`}
              </MenuItem>
            ))}
          </Menu>
          <Divider />
          <MenuItem
            onClick={() => { setAnchorElFile(null); setConfirmDelete(true); }}
            sx={{ color: 'red' }}
          >
            <ListItemIcon><FiTrash2 /></ListItemIcon>
            <Typography>Delete</Typography>
          </MenuItem>
        </Menu>
        {/* AI Tools Menu */}
        {!aiDisabled && (
          <>
            <Button
              variant="text"
              onClick={e => setAnchorElAI(e.currentTarget)}
              sx={{ textTransform: 'none', fontWeight: 'bold', color: 'gray' }}
            >
              Grrr
            </Button>
            <Menu
              anchorEl={anchorElAI}
              open={openAI}
              onClose={() => setAnchorElAI(null)}
            >
              <MenuItem onClick={() => { setAnchorElAI(null); onAddBits && onAddBits(); }}>
                <Typography> Add Bits</Typography>
              </MenuItem>
              <MenuItem onClick={() => { setAnchorElAI(null); onFormat && onFormat(); }}>
                <Typography> Find Laughs</Typography>
              </MenuItem>
              <MenuItem onClick={() => { setAnchorElAI(null); onCoachReview && onCoachReview(); }}>
                <Typography> Grr Review...</Typography>
              </MenuItem>
            </Menu>
          </>
        )}
        {/* Edit Menu: formatting actions - punchline/bit/stage */}
        {mode === "edit" && (
          <>
            <Button
              variant="text"
              onClick={e => setAnchorElEdit(e.currentTarget)}
              sx={{ textTransform: 'none', fontWeight: 'bold', color: 'gray' }}
            >
              Edit
            </Button>
            <Menu
              anchorEl={anchorElEdit}
              open={openEdit}
              onClose={() => setAnchorElEdit(null)}
            >
              <MenuItem
                onClick={() => { setAnchorElEdit(null); onApplyBold && onApplyBold(); }}
                disabled={rawView || !lineFocused}
              >
                <ListItemIcon><LetterIcon>P</LetterIcon></ListItemIcon>
                <Typography>Punchline</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => { setAnchorElEdit(null); onApplyBit && onApplyBit(); }}
                disabled={rawView || !lineFocused}
                selected={isHeaderActive}
              >
                <ListItemIcon><LetterIcon>B</LetterIcon></ListItemIcon>
                <Typography fontWeight={isHeaderActive ? 700 : 400}>
                  {isHeaderActive ? "Remove Bit Header" : "Make Bit Header"}
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => { setAnchorElEdit(null); onApplyStage && onApplyStage(); }}
                disabled={rawView || !lineFocused}
              >
                <ListItemIcon><LetterIcon>S</LetterIcon></ListItemIcon>
                <Typography>Stage Direction</Typography>
              </MenuItem>
            </Menu>
          </>
        )}
        {/* View Menu: switch between the formatted and markup views */}
        {mode === "edit" && (
          <>
            <Button
              variant="text"
              onClick={e => setAnchorElViewMenu(e.currentTarget)}
              sx={{ textTransform: 'none', fontWeight: 'bold', color: 'gray' }}
            >
              View
            </Button>
            <Menu
              anchorEl={anchorElViewMenu}
              open={openViewMenu}
              onClose={() => setAnchorElViewMenu(null)}
            >
              <MenuItem
                onClick={() => { setAnchorElViewMenu(null); onSetRawView && onSetRawView(false); }}
                selected={!rawView}
              >
                <Typography fontWeight={!rawView ? 700 : 400}>Formatted</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => { setAnchorElViewMenu(null); onSetRawView && onSetRawView(true); }}
                selected={rawView}
              >
                <Typography fontWeight={rawView ? 700 : 400}>Markup</Typography>
              </MenuItem>
            </Menu>
          </>
        )}
      </div>
      {/* Mode Toggle Button, flush right */}
      <div className="flex items-center ml-4">
        {/* Pass practice handler to the toggle */}
        <PerformanceModeToggle
          mode={mode}
          onEdit={onEdit}
          onPractice={onPractice}
          onPerform={onPerform}
        />
      </div>
    </div>
  );
}
